import React, { useEffect, useState } from 'react';
import { DatePickerProps } from './types';
import { DateRange, Calendar, RangeKeyDict, Range } from 'react-date-range';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './datePicker.css';
import { DropdownMenu } from '../../form';
import { formatDate } from '../../../helpers/functions';
import { Icon } from '../../UI';
import { useThemeContext } from '../../../context/Theme';

export const DatePicker = ({
  date,
  dateRangeMonths = 2,
  dropdownTooltipXOffset,
  dropdownTooltipYOffset,
  endDate,
  hasBorderBottom = true,
  icon = null,
  id,
  inputClassName = '',
  isRangePicker,
  isDisabled,
  label,
  maxDate,
  minDate,
  onChange,
  placeholder,
  showLabel = false,
  fixedLabel = false,
  size = 'full',
  startDate,
  inputDateFormatter,
  validate,
}: DatePickerProps) => {
  const { theme } = useThemeContext();
  const [dateRangeState, setDateRangeState] = useState<Range[]>([
    {
      startDate: startDate || new Date(),
      endDate: endDate || new Date(),
      key: 'selection',
    },
  ]);
  const [labelShown, setLabelShown] = useState<boolean>(fixedLabel);

  const [dateState, setDateState] = useState<Date | undefined>(date);
  const [isTouched, setIsTouched] = useState<boolean>(false);

  const sizeFullWidth = size === 'full' ? 'w-full' : '';
  const sizeLargeWidth = size === 'large' ? 'w-96' : '';
  const sizeMediumWidth = size === 'medium' ? 'w-64' : '';
  const sizeSmallWidth = size === 'small' ? 'w-44' : '';

  const finalSize =
    sizeFullWidth || sizeLargeWidth || sizeMediumWidth || sizeSmallWidth;

  useEffect(() => {
    if (isRangePicker) {
      setDateRangeState([
        {
          startDate: startDate || new Date(),
          endDate: endDate || new Date(),
          key: 'selection',
        },
      ]);
    } else {
      setDateState(date);
    }
    if (date === null || date === undefined) {
      setIsTouched(false);
    }
    setLabelShown(!isInputNull());
  }, [date, startDate, endDate]);

  const handleDateRangePickerChange = (item: RangeKeyDict) => {
    const { selection } = item;

    setDateRangeState([selection]);
    setLabelShown(true);

    onChange?.([selection.startDate, selection.endDate]);
  };

  const handleDatePickerChange = (item: Date) => {
    setDateState(item);
    setLabelShown(true);
    setIsTouched(true);
    onChange?.(item);
  };

  const isInputNull = () =>
    (isRangePicker && !startDate && !endDate) || (!isRangePicker && !date);

  const formatInputValue = () => {
    if (isInputNull()) {
      return '';
    }

    if (isRangePicker) {
      // CUSTOM FORMATTER
      if (inputDateFormatter) {
        return inputDateFormatter(
          dateRangeState[0].startDate,
          dateRangeState[0].endDate
        );
      }
      return dateRangeState[0]?.startDate && dateRangeState[0]?.endDate
        ? `${formatDate(dateRangeState[0]?.startDate)} - ${formatDate(
            dateRangeState[0]?.endDate
          )}`
        : '';
    }
    if (inputDateFormatter) return inputDateFormatter(dateState || null);
    return dateState ? formatDate(dateState) : '';
  };

  const datePickerComponent = isRangePicker ? (
    <DateRange
      months={dateRangeMonths}
      ranges={dateRangeState}
      onChange={handleDateRangePickerChange}
      direction="horizontal"
      color={theme === 'DARK' ? '#404044' : '#B7FF00'}
      rangeColors={[theme === 'DARK' ? '#404044' : '#B7FF00']}
      minDate={minDate}
      maxDate={maxDate}
    />
  ) : (
    <Calendar
      onChange={handleDatePickerChange}
      date={dateState}
      color={theme === 'DARK' ? '#404044' : '#B7FF00'}
      rangeColors={[theme === 'DARK' ? '#404044' : '#B7FF00']}
      minDate={minDate}
      maxDate={maxDate}
    />
  );

  const isValid = validate?.(dateState);

  return (
    <DropdownMenu
      dropdownClassName={`${finalSize}`}
      // dropdownIconClassName="relative"
      dropdownTooltipClassName={`
        bg-white
        border-solid
        border-grey-10
        border
        shadow-md
        z-80
        w-max
      `}
      dropdownItems={['']}
      dropdownItemsComponents={[datePickerComponent]}
      setDropdownAsStatic
      hideOnItemClick
      hideOnOutsideClick
      dropdownTooltipXOffset={dropdownTooltipXOffset}
      dropdownTooltipYOffset={dropdownTooltipYOffset}
    >
      {showLabel && labelShown && (
        <label
          htmlFor={id}
          className={`absolute -top-4 left-0 text-xs font-medium ${
            isDisabled
              ? 'text-grey-30 dark:text-grey-70'
              : 'text-black dark:text-white'
          } `}
        >
          {label}
        </label>
      )}
      <div
        className={`
          flex
          items-center
          w-full
          ${
            hasBorderBottom && isDisabled
              ? 'border-b border-grey-30'
              : hasBorderBottom && isTouched && validate
              ? isValid
                ? 'border-b border-positive'
                : 'border-b border-negative'
              : hasBorderBottom
              ? 'border-b border-black'
              : ''
          }
        `}
      >
        <input
          type="text"
          value={formatInputValue()}
          placeholder={isInputNull() ? `${placeholder || ''}` : ''}
          className={`
            pb-2
            mt-1.5
            w-full
            cursor-pointer
            border-black
            dark:border-white
            text-grey-70
            dark:text-white
            dark:bg-grey-90
            ${inputClassName}
          `}
          readOnly
        />
        {icon || <Icon svgName="cal" />}
      </div>
    </DropdownMenu>
  );
};

export default DatePicker;
